import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './reagentprep.css';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { LookupCombo } from '../../components/LookupCombo';
import { AiCombo } from '../../components/AiCombo';
import { formatCombo } from '../../components/hotkeys';
import { confirmDiscardIfUnsavedIn } from '../../hooks/useUnsavedGuard';
import { useNotify } from '../../components/notifications';
import { fmtLocalDate } from '../../utils/datetime';
import { api } from '../../api/client';
import { CHEM_COLS } from './componentColumns';
import { CHEMICAL } from './prepForms';
import {
  FIELD_FORMULA_TOKEN, FIELD_STATE_CLASS, FORMULA_TOKEN_FIELD, LOOKUP_GEOMETRY,
  NP_CHEM_COL1, NP_CHEM_COL2, NP_MICRO_COL1, NP_MICRO_COL2,
  SOLVENT_LOCKS_CHEM, SOLVENT_LOCKS_MICRO, type NpField,
} from './npFields';
import { calculateComponent, fetchLookup, type ReagentCalcResult } from './api';
import { blankDraft, type PrepNotepadDraft } from './prepNotepadDraft';
import { NA_OPTION, type ReagentLookupOption, type ReagentPrepComponent } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// The Prep Notepad — the component-entry wizard (spec §9).
//
// The form inside it is the legacy NON-PERSISTENT detail view
// `NPReagentPrepLog_DetailView_Chemistry` / `_MicroMedia` over
// `NPReagentPrepLog`; its field list, order, captions and control types live in
// npFields.ts, transcribed from those views' LAYOUTS. Chemistry has 26 fields
// across two columns, Micro Media 20 — an earlier build shipped six, which is
// what this replaces.
//
// WHEN IT OPENS — the per-category routing:
//   · Classify → New Prep → Chemical              → here FIRST, no parent
//   · Classify → New Prep → Micro Media           → here FIRST, no parent
//   · Classify → New Prep → Calibration Set Prep  → NEVER (no Notepad exists)
//   · Chemistry / Micro header form → Prep Notepad → here, existing components
//
// FOUR GRID-LOOKUPS, each an XAF ASPxGridLookup with its own popup columns,
// criteria and data source — rendered through the shared <LookupCombo>:
//   Vendor Stock  Items_LookupListView                Item·Vendor·Description·Catalog#
//   LT#           Distribution_LookupListView_LT      LT#·VendorLot·ExpiryDate
//   Lab Stock     StandardName_LookupListView         PrepName·PrepType
//   LLT#          ReagentPreparation_LookupListView_  LLT·PrepType·PrepName·
//                   Calibration                       PrepDate·ExpiryDate
// LT# depends on the chosen Vendor Stock and LLT# on the chosen Lab Stock; with
// no parent each is EMPTY, which is legacy behaviour (spec §6.1).
//
// ⚠️ STILL MISSING — the CALCULATION ENGINE (spec §10.3). The approach, unit and
// value fields are all present and editable, and Formula is shown read-only, but
// nothing yet MATCHES a Calculation Editor row or EVALUATES it, so Formula stays
// empty and no target field is auto-computed or painted Yellow/LightYellow
// (§14.2). That is an omission from XAF, tracked in the migration log.
// ─────────────────────────────────────────────────────────────────────────────

/** A saved component → a draft, so reopening the Notepad shows what is stored. */
function toDraft(c: ReagentPrepComponent, fallbackId: number): PrepNotepadDraft {
  const d = blankDraft(Number(c.componentID ?? fallbackId));
  return {
    ...d,
    vendorStockId: c.vendorStockId ?? '', vendorStockName: c.vendorStockName ?? '',
    ltId: c.ltId ?? '', ltText: c.ltText ?? '', ltExpDate: c.ltExpDate ?? '',
    labStockId: c.labStockId ?? '', labStockName: c.labStockName ?? '',
    lltId: c.lltId ?? '', lltText: c.lltText ?? '', lltExpDate: c.lltExpDate ?? '',
    solvent: !!c.solvent,
    calculationApproachId: c.calculationApproachId ?? '',
    w1UnitsId: c.w1UnitsId ?? '', v1UnitsId: c.v1UnitsId ?? '',
    c1UnitsId: c.c1UnitsId ?? '', v2UnitsId: c.v2UnitsId ?? '', c2UnitsId: c.c2UnitsId ?? '',
    purity: c.purity ?? '', density: c.density ?? '', mw: c.mw ?? '',
    eqWt: c.eqWt ?? '', constant: c.constant ?? '',
    formula: c.formula ?? '',
    weight_g_w1: c.weight_g_w1 ?? '', volumeTaken_V1: c.volumeTaken_V1 ?? '',
    stockConc_C1: c.stockConc_C1 ?? '', finalVol_V2: c.finalVol_V2 ?? '',
    finalConc_C2: c.finalConc_C2 ?? '',
    comment: c.comment ?? '',
    phCriteria: c.phCriteria ?? '', ph: c.ph ?? '', autoclave: c.autoclave ?? '',
    filterSterilization: c.filterSterilization ?? '', sporeGrowth: c.sporeGrowth ?? '',
    positiveControl: c.positiveControl ?? '', negativeControl: c.negativeControl ?? '',
  };
}

interface GridRow { id: string; cells: string[] }

interface Props {
  prepSelectType: number;
  existing?: ReagentPrepComponent[];
  /** Parent form is in View mode: everything read-only, Reset / OK disabled;
   *  Previous / Next browse the stored components only (XAF ctrl :153 / :5544). */
  viewOnly?: boolean;
  /** Open ON this component (its ComponentID) instead of the first — a click on
   *  a row of the components grid opens the Notepad on that row's data. */
  startComponentId?: number | null;
  onOk: (components: PrepNotepadDraft[]) => void;
  onClose: () => void;
}

/** The Notepad grid leads with the component ID captioned "ID": XAF builds it
 *  from a DataTable whose first column is "ID" (CreateDataTableInChemistry /
 *  CreateDataTableInMicroMedia), unlike the detail page's model ListView. */
const ID_COL: AppColumn<ReagentPrepComponent> = { field: 'componentID', header: 'ID', width: 50 };
const NOTEPAD_CHEM_COLS = [ID_COL, ...CHEM_COLS];
/** XAF's Micro Notepad grid is its working table minus the Oid columns
 *  (CreateDataTableInMicroMedia :596), in that order and with those captions. */
const NOTEPAD_MICRO_COLS: AppColumn<ReagentPrepComponent>[] = [
  ID_COL,
  { field: 'vendorStockName',     header: 'VendorStock',              width: 150 },
  { field: 'ltText',              header: 'LT#',                      width: 90 },
  { field: 'labStockName',        header: 'LabStock',                 width: 180 },
  { field: 'lltText',             header: 'LLT#',                     width: 110 },
  { field: 'weight_g_w1',         header: 'WtTaken(g)',               width: 90 },
  { field: 'volumeTaken_V1',      header: 'VolumeTaken(ml)',          width: 110 },
  { field: 'finalVol_V2',         header: 'FinalVolume(ml)',          width: 110 },
  { field: 'filterSterilization', header: 'FilterSterilization(Y/N)', width: 150 },
  { field: 'sporeGrowth',         header: 'SporeGrowth(Y/N)',         width: 120 },
  { field: 'phCriteria',          header: 'PHCriteria',               width: 90 },
  { field: 'ph',                  header: 'PH',                       width: 60 },
  { field: 'positiveControl',     header: '+Control',                 width: 70 },
  { field: 'negativeControl',     header: '-Control',                 width: 70 },
  { field: 'autoclave',           header: 'Autoclave(Y/N)',           width: 100 },
  { field: 'solvent',             header: 'Solvent',                  width: 60 },
  { field: 'ltExpDate',           header: 'LT#ExpDate',               width: 100, type: 'date' },
  { field: 'lltExpDate',          header: 'LLT#ExpDate',              width: 100, type: 'date' },
];

export function PrepNotepadDialog({
  prepSelectType, existing = [], viewOnly = false, startComponentId = null, onOk, onClose,
}: Props) {
  const notify = useNotify();
  const isChem = prepSelectType === CHEMICAL;

  /** The popup body — the scope of the close-time unsaved-changes check. */
  const bodyRef = useRef<HTMLDivElement>(null);
  const [buffer, setBuffer]   = useState<PrepNotepadDraft[]>(() => existing.map((c, i) => toDraft(c, i + 1)));
  const [current, setCurrent] = useState<PrepNotepadDraft>(() => {
    // The clicked row when one was given, else the first component.
    const at = startComponentId == null ? 0
      : Math.max(0, existing.findIndex((c) => Number(c.componentID) === startComponentId));
    const pick = existing[at];
    return pick ? toDraft(pick, at + 1) : blankDraft(1);
  });

  // Simple dropdown feeds (units, calculation approaches) — Chemistry only.
  const [units, setUnits]         = useState<ReagentLookupOption[]>([]);
  const [approaches, setApproach] = useState<ReagentLookupOption[]>([]);
  // Grid-lookup feeds, keyed by lookup name.
  const [grids, setGrids] = useState<Record<string, GridRow[]>>({});

  useEffect(() => {
    if (!isChem) return;
    void (async () => {
      try {
        const [u, a] = await Promise.all([fetchLookup('units'), fetchLookup('approaches')]);
        setUnits(u); setApproach(a);
      } catch (e) {
        notify.error(e instanceof Error ? e.message : 'Failed to load the dropdown lists.');
      }
    })();
  }, [isChem, notify]);

  /**
   * Fetch one grid-lookup. `parentId` narrows LT# / LLT#; without it the server
   * returns an empty list, matching legacy (spec §6.1).
   *
   * A FAILURE IS SURFACED, never swallowed. An earlier version caught
   * everything and set `[]`, so a 404 from a stale API build was indistinguishable
   * from "this item has no lots" — the picker just sat there empty with no clue
   * why. The only silent case now is the legitimate one: no parent selected.
   */
  const loadGrid = useCallback(async (kind: string, parentId?: string) => {
    try {
      const res = await api.get<{ data?: Array<{ id: string; name: string; cols?: string[] }> }>(
        `/api/v1/reagent-preps/grid-lookups/${kind}`,
        { params: parentId ? { parentId } : undefined },
      );
      const rows = res.data?.data;
      if (!Array.isArray(rows)) {
        // A 200 that is not the envelope means the route did not match and the
        // SPA fallback served index.html — i.e. the API is older than this UI.
        throw new Error(`Lookup "${kind}" did not return data. The API may be out of date — restart it.`);
      }
      setGrids((g) => ({
        ...g,
        [kind]: rows.map((o) => ({ id: o.id, cells: [o.name, ...(o.cols ?? [])] })),
      }));
    } catch (e) {
      setGrids((g) => ({ ...g, [kind]: [] }));
      notify.error(e instanceof Error ? e.message : `Failed to load the ${kind} list.`);
    }
  }, [notify]);

  // The two independent lookups load once; the dependent two follow their parent.
  useEffect(() => { void loadGrid('vendor-stock'); void loadGrid('lab-stock'); }, [loadGrid]);
  useEffect(() => { void loadGrid('lt',  current.vendorStockId || undefined); }, [loadGrid, current.vendorStockId]);
  useEffect(() => { void loadGrid('llt', current.labStockId   || undefined); }, [loadGrid, current.labStockId]);

  /** True once the analyst changes anything here — every user edit goes through
   *  set() (the calculation engine writes with setCurrent, so it never counts).
   *  Closing without OK then asks first (standing rule 15). */
  const userEdited = useRef(false);
  function set(p: Partial<PrepNotepadDraft>) { userEdited.current = true; setCurrent((c) => ({ ...c, ...p })); }

  // ── Calculation engine (spec §10.3) ────────────────────────────────────────
  //
  // The arithmetic is NOT done here. This effect gathers what the analyst has
  // typed, asks the API, and applies the answer — so the same inputs give the
  // same bench number from any client (CLAUDE.md §1). Chemistry only: Micro
  // Media has no approach or unit pickers.

  const [calc, setCalc] = useState<ReagentCalcResult | null>(null);

  // The field the last answer WRITES. Held in state (not read off `calc` inside
  // the payload) so the request can leave that field out: including it would
  // make applying a result change the payload, which would fire the effect
  // again on every keystroke for no new information.
  const targetField = calc?.target ? FORMULA_TOKEN_FIELD[calc.target] : undefined;

  const calcPayload = useMemo(() => {
    const v = (k: string) => {
      if (k === targetField) return null;   // the engine writes it; it never reads it
      const raw = (current as unknown as Record<string, unknown>)[k];
      return raw == null || raw === '' ? null : String(raw);
    };
    return {
      calculationApproachId: current.calculationApproachId || null,
      v1UnitsId: current.v1UnitsId || null,
      w1UnitsId: current.w1UnitsId || null,
      c1UnitsId: current.c1UnitsId || null,
      v2UnitsId: current.v2UnitsId || null,
      c2UnitsId: current.c2UnitsId || null,
      volumeTaken_V1: v('volumeTaken_V1'),
      weight_g_w1:    v('weight_g_w1'),
      stockConc_C1:   v('stockConc_C1'),
      finalVol_V2:    v('finalVol_V2'),
      finalConc_C2:   v('finalConc_C2'),
      purity:         v('purity'),
      density:        v('density'),
      mw:             v('mw'),
      eqWt:           v('eqWt'),
      constant:       v('constant'),
    };
  }, [current, targetField]);

  const calcKey = JSON.stringify(calcPayload);

  useEffect(() => {
    if (!isChem) return;

    // Typing is continuous; the request should not be. A short debounce keeps
    // one call per pause rather than one per character.
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const res = await calculateComponent(JSON.parse(calcKey));
          setCalc(res);

          // Write the answer into its field, and mirror the matched rule into
          // the read-only Formula box the way legacy does.
          setCurrent((c) => {
            const next: Partial<PrepNotepadDraft> = {};
            const formula = res.formula ?? '';
            if (c.formula !== formula) next.formula = formula;

            const field = res.target ? FORMULA_TOKEN_FIELD[res.target] : undefined;
            if (field) {
              // A null result is a BLANK, not a zero — the analyst has not
              // finished entering the inputs, and a 0 in a dilution is a wrong
              // answer that looks like a real one.
              const shown = res.value == null ? '' : String(res.value);
              if ((c as unknown as Record<string, unknown>)[field] !== shown) {
                (next as Record<string, unknown>)[field] = shown;
              }
            }
            return Object.keys(next).length ? { ...c, ...next } : c;
          });
        } catch {
          // A calculation that cannot be reached must not block data entry:
          // the analyst keeps typing and the fields stay open. The shared axios
          // client has already logged it.
          setCalc(null);
        }
      })();
    }, 250);

    return () => clearTimeout(timer);
  }, [isChem, calcKey]);

  /** ReagentFieldState declaration order (Input = 0, Result = 1, NotApplicable = 2). */
  const FIELD_STATE_BY_INDEX = ['Input', 'Result', 'NotApplicable'] as const;

  /** How a measurement field should behave, per the engine's last answer. */
  function fieldState(key: string): 'Input' | 'Result' | 'NotApplicable' {
    const token = FIELD_FORMULA_TOKEN[key];
    if (!token || !calc?.matched) return 'Input';
    // The API sends the NAME. A number (an API built before 2026-10-22) is
    // mapped by the enum's order; anything unrecognised stays EDITABLE —
    // locking on an unknown value froze every field while painting them yellow.
    const state = calc.fieldStates?.[token] as unknown;
    if (typeof state === 'number') return FIELD_STATE_BY_INDEX[state] ?? 'Input';
    return state === 'Result' || state === 'NotApplicable' ? state : 'Input';
  }

  /** A component must have Vendor Stock OR Lab Stock (spec §6.3 / §9.2). */
  const hasStock = (d: PrepNotepadDraft) => d.vendorStockId !== '' || d.labStockId !== '';

  function commit(d: PrepNotepadDraft): PrepNotepadDraft[] {
    const next = [...buffer];
    const at = next.findIndex((x) => x.componentID === d.componentID);
    if (at >= 0) next[at] = d; else next.push(d);
    next.sort((a, b) => a.componentID - b.componentID);
    setBuffer(next);
    return next;
  }

  function load(id: number, from: PrepNotepadDraft[]) {
    if (id < 1) return;
    // Drop the previous component's calculation. Without this the new component
    // would briefly wear the old one's field states — greying a box the analyst
    // is allowed to type in — until the next answer arrives.
    setCalc(null);
    setCurrent(from.find((x) => x.componentID === id) ?? blankDraft(id));
  }

  function goNext() {
    if (viewOnly) { load(current.componentID + 1, buffer); return; }
    if (!hasStock(current)) {
      // spec §18 msg 4. It BLOCKS moving on, so it is a warning box the analyst
      // acknowledges (two-tier standard), not a banner that fades out (2026-11-03).
      notify.warning('Please select vendor stock or lab stock.');
      return;
    }
    const next = commit(current);
    load(current.componentID + 1, next);
  }

  function goPrev() {
    // No validation on Previous (spec §9.2). View mode only browses.
    const next = !viewOnly && hasStock(current) ? commit(current) : buffer;
    load(current.componentID - 1, next);
  }

  /** Reset clears the CURRENT component except ID and Solvent (spec §9.2). */
  function reset() {
    setCurrent({ ...blankDraft(current.componentID), solvent: current.solvent });
  }

  function ok() {
    const final = hasStock(current) ? commit(current) : buffer;
    if (final.length === 0) {
      notify.error("You haven't added any component yet.");      // spec §18 msg 5
      return;
    }
    onOk(final);
  }

  // Selecting one stock CLEARS the other, with its lot and exp date (§6.3).
  function pickVendorStock(row: GridRow | null) {
    set({
      vendorStockId: row?.id ?? '', vendorStockName: row?.cells[0] ?? '',
      ltId: '', ltText: '', ltExpDate: '',
      labStockId: '', labStockName: '', lltId: '', lltText: '', lltExpDate: '',
    });
  }
  function pickLabStock(row: GridRow | null) {
    set({
      labStockId: row?.id ?? '', labStockName: row?.cells[0] ?? '',
      lltId: '', lltText: '', lltExpDate: '',
      vendorStockId: '', vendorStockName: '', ltId: '', ltText: '', ltExpDate: '',
      // Chemistry only: a lab stock cannot be the solvent, so the tick clears and
      // locks (§6.3, XAF ObjectChanged :1631). The Micro handler (:2233) clears
      // the other stock and its lot but leaves Solvent alone.
      solvent: row && isChem ? false : current.solvent,
    });
  }

  const locks = isChem ? SOLVENT_LOCKS_CHEM : SOLVENT_LOCKS_MICRO;
  const ro = viewOnly;
  // View mode only browses what exists: XAF turns Next off at the last stored
  // component (ctrl :153 / :5494) — it never opens a blank one there.
  const atLastStored = viewOnly && !buffer.some((x) => x.componentID === current.componentID + 1);

  function renderField(f: NpField) {
    // MW / EqWt appear only when the matched Formula mentions them (§4.2).
    if (f.onlyWhenFormulaHas && !current.formula.includes(f.onlyWhenFormulaHas)) return null;

    const locked = ro || (current.solvent && locks.has(f.key));
    const raw = (current as unknown as Record<string, unknown>)[f.key];
    const str = raw == null ? '' : String(raw);

    let control: React.ReactNode;
    switch (f.kind) {
      case 'readonly': {
        // The two Exp Dates are DERIVED mirrors of the chosen lot (§4.2).
        const shown = f.key.endsWith('ExpDate') && str ? fmtLocalDate(str) : str;
        control = <input type="text" className="sd-input readonly" readOnly value={shown} />;
        break;
      }
      case 'numeric': {
        // The calculation engine owns this field's state once a formula has
        // matched: it is an INPUT the formula reads, the RESULT it writes, or
        // NOT APPLICABLE to this formula at all (spec §10.3).
        const state = fieldState(f.key);
        const engineLocked = state !== 'Input';
        const readOnly = locked || engineLocked;
        const cls =
          state === 'Result'        ? FIELD_STATE_CLASS.formulaResult
          : state === 'NotApplicable' ? FIELD_STATE_CLASS.notApplicable
          : `${FIELD_STATE_CLASS.formulaInput}${locked ? ' readonly' : ''}`;

        control = (
          <input
            type="text" inputMode="decimal" className={cls}
            readOnly={readOnly} value={str}
            // Tell the analyst WHY a field is closed rather than leaving them to
            // guess at a greyed box.
            title={
              state === 'Result' ? `Calculated${calc?.codeId ? ` by ${calc.codeId}` : ''}.`
              : state === 'NotApplicable' ? 'Not used by the selected calculation.'
              : undefined
            }
            // Numeric-only input (spec §10.2): digits and '.' only, so a
            // negative cannot be typed — legacy parity.
            onChange={(e) => set({ [f.key]: e.target.value.replace(/[^0-9.]/g, '') } as unknown as Partial<PrepNotepadDraft>)}
          />
        );
        break;
      }
      case 'text':
        control = <input type="text" className={`sd-input${locked ? ' readonly' : ''}`}
                         readOnly={locked} value={str}
                         onChange={(e) => set({ [f.key]: e.target.value } as unknown as Partial<PrepNotepadDraft>)} />;
        break;
      case 'checkbox':
        control = <input type="checkbox" checked={current.solvent}
                         // Chemistry: read-only while a Lab Stock is chosen (§6.3).
                         // Micro has no such lock in XAF (:2233).
                         disabled={ro || (isChem && current.labStockId !== '')}
                         onChange={(e) => set({ solvent: e.target.checked })} />;
        break;
      case 'formula':
        // Read-only: copied from the matched Calculation Editor row (§4.2).
        control = <textarea className="sd-input readonly" readOnly rows={3} value={current.formula} />;
        break;
      case 'comment':
        control = <textarea className={`sd-input${locked ? ' readonly' : ''}`} rows={4}
                            readOnly={locked} value={current.comment}
                            onChange={(e) => set({ comment: e.target.value })} />;
        break;
      case 'units':
      case 'approach': {
        // The shared <AiCombo> — the same combo as Department's Parent and the
        // Reagent Prep detail (drop arrow + the XAF eraser for an FK), owner
        // request 2026-11-03. It draws its own list (a native <select> list is
        // not styleable), capped at FIVE rows by `visibleRows` (owner
        // request 2026-09-26). Its top "N/A" row clears ('' → null on save).
        const opts = f.kind === 'units' ? units : approaches;
        control = (
          <AiCombo
            value={str || null}
            options={opts.map((o) => ({ value: o.id, label: o.name ?? '' }))}
            disabled={locked}
            placeholder={NA_OPTION.label}
            erasable
            // The clear this field already had, drawn as Sample Registration's inline ⊗
            // instead of the eraser block (owner request 2026-11-03).
            clearInline
            ariaLabel={f.label}
            visibleRows={5}
            onChange={(v) => set({ [f.key]: v ?? '' } as unknown as Partial<PrepNotepadDraft>)}
          />
        );
        break;
      }
      case 'grid': {
        const rows = grids[f.lookup!] ?? [];
        const shown =
          f.lookup === 'vendor-stock' ? current.vendorStockName
          : f.lookup === 'lt'         ? current.ltText
          : f.lookup === 'lab-stock'  ? current.labStockName
          :                             current.lltText;
        // Identified by the row's OWN Oid, never by the (repeating) name.
        const idOfRow = new Map(rows.map((r) => [r.cells, r.id] as const));
        const selectedId =
          f.lookup === 'vendor-stock' ? current.vendorStockId
          : f.lookup === 'lt'         ? current.ltId
          : f.lookup === 'lab-stock'  ? current.labStockId
          :                             current.lltId;
        control = (
          <LookupCombo
            lookup={{ cols: f.cols ?? ['Name'], rows: rows.map((r) => r.cells) }}
            value={selectedId}
            rowKey={(row) => idOfRow.get(row) ?? ''}
            labelText={shown}
            disabled={locked}
            popupWidth={LOOKUP_GEOMETRY[f.lookup!]?.popup}
            colWidths={LOOKUP_GEOMETRY[f.lookup!]?.widths}
            wrapCells
            // N/A first: a client-only placeholder that CLEARS the pick (and the
            // lot/expiry that depend on it) — never saved as a value.
            naOption
            // Plain text value — no grey label chip (owner request 2026-10-16).
            plainValue
            onClear={() => {
              if (f.lookup === 'vendor-stock') pickVendorStock(null);
              else if (f.lookup === 'lab-stock') pickLabStock(null);
              else if (f.lookup === 'lt') set({ ltId: '', ltText: '', ltExpDate: '' });
              else set({ lltId: '', lltText: '', lltExpDate: '' });
            }}
            onPick={(row) => {
              // Names repeat, so the row identity carries the id back.
              // By row IDENTITY only — never fall back to the name, which repeats.
              const found = rows.find((r) => r.cells === row) ?? null;
              const id = found?.id ?? '';
              if (f.lookup === 'vendor-stock') pickVendorStock(found);
              else if (f.lookup === 'lab-stock') pickLabStock(found);
              // LT# / LLT# carry their expiry across as the read-only mirror.
              else if (f.lookup === 'lt')
                set({ ltId: id, ltText: row[0] ?? '', ltExpDate: row[2] ?? '' });
              else
                set({ lltId: id, lltText: row[0] ?? '', lltExpDate: row[4] ?? '' });
            }}
          />
        );
        break;
      }
    }

    return (
      <div className="rgp-field" key={f.key}>
        {/* MW / EqWt captions render red in XAF — they appear only when the
            formula needs them, so the colour is the cue that one is required. */}
        <label className={`rgp-label sd-label${f.onlyWhenFormulaHas ? ' rgp-label--alert' : ''}`}>
          {/* Trimmed: some XAF captions end in a space ("Volume Taken (V1) "), which
              would put a gap before the colon the stylesheet adds. */}
          {f.label.trim()}
        </label>
        <div className="rgp-control">{control}</div>
      </div>
    );
  }

  const col1 = isChem ? NP_CHEM_COL1 : NP_MICRO_COL1;
  const col2 = isChem ? NP_CHEM_COL2 : NP_MICRO_COL2;

  // The buffer rendered through the SAME column set the header form's child grid
  // uses, so the two can never disagree about a component.
  const gridRows = useMemo(() => buffer.map((d) => ({
    oid: String(d.componentID),
    componentID: d.componentID,
    vendorStockName: d.vendorStockName || null,
    ltText: d.ltText || null, ltExpDate: d.ltExpDate || null,
    labStockName: d.labStockName || null,
    lltText: d.lltText || null, lltExpDate: d.lltExpDate || null,
    solvent: d.solvent,
    calculationApproachName: approaches.find((a) => a.id === d.calculationApproachId)?.name ?? null,
    v1UnitsName: units.find((u) => u.id === d.v1UnitsId)?.name ?? null,
    w1UnitsName: units.find((u) => u.id === d.w1UnitsId)?.name ?? null,
    c1UnitsName: units.find((u) => u.id === d.c1UnitsId)?.name ?? null,
    v2UnitsName: units.find((u) => u.id === d.v2UnitsId)?.name ?? null,
    c2UnitsName: units.find((u) => u.id === d.c2UnitsId)?.name ?? null,
    stockConc_C1: d.stockConc_C1 || null, volumeTaken_V1: d.volumeTaken_V1 || null,
    weight_g_w1: d.weight_g_w1 || null, finalVol_V2: d.finalVol_V2 || null,
    finalConc_C2: d.finalConc_C2 || null,
    purity: d.purity || null, density: d.density || null,
    mw: d.mw || null, eqWt: d.eqWt || null, constant: d.constant || null,
    formula: d.formula || null, comment: d.comment || null,
    phCriteria: d.phCriteria || null, ph: d.ph || null, autoclave: d.autoclave || null,
    filterSterilization: d.filterSterilization || null, sporeGrowth: d.sporeGrowth || null,
    positiveControl: d.positiveControl || null, negativeControl: d.negativeControl || null,
  })) as unknown as ReagentPrepComponent[], [buffer, units, approaches]);

  // Esc / ✕ / backdrop: ask first when something typed in THIS popup would be lost
  // (standing rule 15) — scoped to the popup, so the page behind is neither asked
  // about nor forgotten. OK commits, so it never asks.
  const requestClose = () => {
    void confirmDiscardIfUnsavedIn(bodyRef.current, userEdited.current).then((leave) => { if (leave) onClose(); });
  };
  const prevDisabled = current.componentID <= 1;

  return (
    <AppDialog
      open
      title="Prep Notepad"
      width={1350}
      className="dlg-center"
      onClose={requestClose}
      maximizable
      // XAF hides the dialog's own OK / Cancel; the wizard set is the only one
      // (ctrl :140-141). It sits in the dialog FOOTER, bottom right (owner request
      // 2026-11-03), so the shared AppDialog's Enter and Ctrl+S press OK (the
      // footer's .ca-btn-solid) — standing rule 14 — with no per-dialog code.
      hotkeys={[
        { combo: 'alt+left', run: () => goPrev(), enabled: !prevDisabled },
        { combo: 'alt+right', run: () => goNext(), enabled: !atLastStored },
      ]}
      footer={
        <>
          {/* OK · Previous · Next · Reset — Reset LAST (owner request 2026-11-03;
              legacy draws it first, apart on the left). */}
          <button type="button" className="ca-btn ca-btn-solid" onClick={ok} disabled={ro}
                  title={`OK (${formatCombo('mod+s')} / Enter)`}>OK</button>
          {/* Previous is off only on the first component — XAF re-enables it
              after every Next, in view mode too (ctrl :5544 / :5641). */}
          <button type="button" className="ca-btn" onClick={goPrev} disabled={prevDisabled}
                  title={`Previous (${formatCombo('alt+left')})`}>Previous</button>
          <button type="button" className="ca-btn" onClick={goNext} disabled={atLastStored}
                  title={`Next (${formatCombo('alt+right')})`}>Next</button>
          <button type="button" className="ca-btn" onClick={reset} disabled={ro} title="Reset">Reset</button>
        </>
      }
    >
      <div className="rgp-np" ref={bodyRef}>
        {/* Plain headings, no frames — as the detail's "Basic Information". */}
        <div className="sd-section-h">{isChem ? 'General Chemistry' : 'MicroMedia'}</div>
        <div className="rgp-cols">
          <div className="rgp-col">{col1.map(renderField)}</div>
          <div className="rgp-col">{col2.map(renderField)}</div>
        </div>

        <div className="sd-section-h rgp-np-grid-h">Components</div>
        <AppDataGrid<ReagentPrepComponent>
          rows={gridRows}
          columns={isChem ? NOTEPAD_CHEM_COLS : NOTEPAD_MICRO_COLS}
          getRowId={(r) => r.oid}
          emptyText=""
        />
      </div>
    </AppDialog>
  );
}
